import { NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { cargarTextosRecordatorio } from "@/components/ApptDialog";
import { LayoutDashboard, Users, CheckCircle2, Eye, CalendarDays, CalendarRange, UserCog, LogOut, BarChart3, CalendarClock, FileText, Globe, ClipboardCheck, Euro } from "lucide-react";
import { useAuth, puedeEco } from "@/lib/auth";
import { TIPOS, CENTROS } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { NewPatientDialog } from "@/components/NewPatientDialog";

const NAV = [
  { to: "/", label: "Panel principal", icon: LayoutDashboard, id: "dashboard" },
  { to: "/agenda", label: "Agenda", icon: CalendarRange, id: "agenda" },
  { to: "/pacientes", label: "Control de pacientes", icon: Users, id: "pacientes" },
  ...Object.entries(TIPOS).map(([k, t]) => ({ to: `/tratamientos/${k}`, label: t.label, icon: t.icon, id: k })),
  { to: "/altas", label: "Protocolos de alta", icon: CheckCircle2, id: "altas" },
  { to: "/revisiones", label: "Revisiones programadas", icon: CalendarClock, id: "revisiones" },
  { to: "/control-terapia", label: "Revisiones de terapia", icon: ClipboardCheck, id: "control-terapia" },
  { to: "/presupuestos", label: "Presupuestos", icon: FileText, id: "presupuestos" },
  { to: "/solicitudes", label: "Solicitudes web", icon: Globe, id: "solicitudes" },
  { to: "/planillas", label: "Semana tipo", icon: CalendarDays, id: "planillas" },
  { to: "/estadisticas", label: "Estadísticas", icon: BarChart3, id: "estadisticas" },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors ${
    isActive ? "bg-[#1E3A8A] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  }`;

const CentroSelect = () => {
  const { centro, setCentro } = useCentro();
  return (
    <select data-testid="centro-selector" className="field-select" style={{ width: 180 }} value={centro} onChange={(e) => setCentro(e.target.value)}>
      <option value="">Todos los centros</option>
      {CENTROS.map((c) => <option key={c} value={c}>{c === "MADRID" ? "Madrid" : "Alcobendas"}</option>)}
    </select>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const [textosOk, setTextosOk] = useState(false);
  useEffect(() => {
    cargarTextosRecordatorio().catch(() => {}).finally(() => setTextosOk(true));
  }, []);
  const base = puedeEco(user) ? [...NAV, { to: "/economico", label: "Económico", icon: Euro, id: "economico" }] : NAV;
  const nav = user?.role === "admin" ? [...base, { to: "/equipo", label: "Equipo", icon: UserCog, id: "equipo" }] : base;
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <aside className="no-print lg:w-60 lg:min-h-screen bg-white border-b lg:border-b-0 lg:border-r border-slate-200 lg:sticky lg:top-0 lg:h-screen">
        <div className="px-5 py-5 flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-[#0D9488] grid place-items-center"><Eye className="h-4 w-4 text-white" /></div>
          <div>
            <div className="font-[Outfit] font-bold text-slate-900 leading-tight">Flujo Pacientes</div>
            <div className="text-[11px] text-slate-500">Terapia visual</div>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-1 px-3 pb-3 overflow-x-auto" data-testid="main-nav">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.id}`}>
              <n.icon className="h-4 w-4 shrink-0" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="no-print sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-slate-200 px-4 sm:px-8 h-14 flex items-center justify-end gap-3">
          <CentroSelect />
          <NewPatientDialog />
          <span className="text-xs text-slate-500 hidden md:inline" data-testid="current-user">{user?.nombre}</span>
          {user?.auth_enabled && <button data-testid="logout-btn" onClick={logout} title="Salir" className="text-slate-400 hover:text-slate-900"><LogOut className="h-4 w-4" /></button>}
        </header>
        <main className="print-list px-4 sm:px-8 py-6 print:p-0">
          {textosOk ? <Outlet /> : null}
        </main>
      </div>
    </div>
  );
}
